import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import { fetchBlogPosts } from '../services/blogService';
import { BlogPage, BlogPost } from '../types/blog';
import { displayCategoryName } from '../utils/blogCategory';
import '../styles/blog-page.css';

function formatPublishedDate(dateIso: string | null): string {
    if (!dateIso) {
        return '';
    }
    const date = new Date(dateIso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function BlogCard({ post }: { post: BlogPost }) {
    const sanitizedExcerpt = useMemo(
        () => (post.excerpt ? DOMPurify.sanitize(post.excerpt) : ''),
        [post.excerpt]
    );
    const categoryName = displayCategoryName(post.categoryName);

    return (
        <article className="blog-card">
            <Link to={`/blog/${post.slug}`} className="blog-card-media-link">
                {post.imageUrl ? (
                    <img
                        src={post.imageUrl}
                        alt={post.title}
                        className="blog-card-image"
                        loading="lazy"
                    />
                ) : (
                    <div className="blog-card-image-placeholder" aria-hidden="true" />
                )}
            </Link>
            <div className="blog-card-body">
                {categoryName && (
                    <p className="blog-card-category">{categoryName}</p>
                )}
                <h2 className="blog-card-title">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h2>
                {formatPublishedDate(post.publishedDate) && (
                    <p className="blog-card-date">{formatPublishedDate(post.publishedDate)}</p>
                )}
                {sanitizedExcerpt && (
                    <div
                        className="blog-card-excerpt"
                        dangerouslySetInnerHTML={{ __html: sanitizedExcerpt }}
                    />
                )}
                <Link to={`/blog/${post.slug}`} className="blog-card-read-more">
                    Read More
                </Link>
            </div>
        </article>
    );
}

export default function BlogListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const pageParam = Number.parseInt(searchParams.get('page') || '1', 10);
    const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

    const [blogPage, setBlogPage] = useState<BlogPage | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadPosts = async () => {
            setIsLoading(true);
            setLoadError(false);

            const result = await fetchBlogPosts(page);

            if (!isMounted) {
                return;
            }

            setBlogPage(result);
            setLoadError(result === null);
            setIsLoading(false);
        };

        loadPosts();

        return () => {
            isMounted = false;
        };
    }, [page]);

    const goToPage = (nextPage: number) => {
        if (nextPage <= 1) {
            setSearchParams({});
        } else {
            setSearchParams({ page: String(nextPage) });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = blogPage?.totalPages ?? 0;
    const hasNewer = page > 1;
    const hasOlder = totalPages > 0 && page < totalPages;

    return (
        <main className="blog-page">
            <PublicHeader />

            <section className="blog-list-section">
                <div className="blog-list-inner">
                    <header className="blog-list-header">
                        <h1 className="blog-list-heading">Blog</h1>
                        <p className="blog-list-subheading">
                            Insights on college planning, NIL education, and athlete development.
                        </p>
                    </header>

                    {isLoading && (
                        <div className="blog-status">Loading posts…</div>
                    )}

                    {!isLoading && loadError && (
                        <div className="blog-status blog-status-error">
                            Unable to load blog posts right now. Please try again later.
                        </div>
                    )}

                    {!isLoading && !loadError && blogPage && blogPage.items.length === 0 && (
                        <div className="blog-status">No blog posts published yet.</div>
                    )}

                    {!isLoading && !loadError && blogPage && blogPage.items.length > 0 && (
                        <>
                            <div className="blog-grid">
                                {blogPage.items.map((post) => (
                                    <BlogCard key={post.id} post={post} />
                                ))}
                            </div>

                            {(hasNewer || hasOlder) && (
                                <nav className="blog-pagination" aria-label="Blog pagination">
                                    {hasNewer ? (
                                        <button
                                            type="button"
                                            className="blog-pagination-link"
                                            onClick={() => goToPage(page - 1)}
                                        >
                                            &lt; Newer Posts
                                        </button>
                                    ) : (
                                        <span />
                                    )}
                                    {hasOlder ? (
                                        <button
                                            type="button"
                                            className="blog-pagination-link"
                                            onClick={() => goToPage(page + 1)}
                                        >
                                            Older Posts &gt;
                                        </button>
                                    ) : (
                                        <span />
                                    )}
                                </nav>
                            )}
                        </>
                    )}
                </div>
            </section>

            <PublicFooter />
        </main>
    );
}
